import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Users, Gift, Info } from 'lucide-react';
import Footer from './Footer';

interface ReferralCodePolicyPageProps {
  onBack: () => void;
}

const ReferralCodePolicyPage: React.FC<ReferralCodePolicyPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 flex items-center justify-center p-4 relative">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, type: "spring" }}
        className="max-w-4xl w-full"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2, type: "spring" }}
            className="w-20 h-20 bg-primary-100 rounded-full mx-auto mb-6 flex items-center justify-center"
          >
            <Users className="w-10 h-10 text-primary-600" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-4xl sm:text-5xl font-bold text-white mb-4"
          >
            Referral Code Policy
          </motion.h1>
        </div>

        {/* Policy Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="card mb-8"
        >
          <div className="prose prose-lg max-w-none">
            {/* Overview */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Info className="w-6 h-6 text-primary-600 mr-3" />
                Overview
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Our referral program rewards users who help grow our community. By sharing your unique referral code, 
                you can improve your position on the waitlist when your referrals successfully sign up with a verified email.
              </p>
            </div>

            {/* Referral Code Types and Rewards */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Gift className="w-6 h-6 text-primary-600 mr-3" />
                Referral Code Types and Rewards
              </h2>
              
              <div className="space-y-6">
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">1. Email Verification Referral Codes</h3>
                  <ul className="text-blue-800 space-y-1">
                    <li>• Users who complete email verification receive a referral code.</li>
                    <li>• A total of 2 successful referrals (a new user completing email signup) improves your waitlist position by 1 spot.</li>
                  </ul>
                </div>
                
                <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg">
                  <h3 className="text-lg font-semibold text-green-900 mb-2">2. Feedback Referral Codes</h3>
                  <ul className="text-green-800 space-y-1">
                    <li>• Users who answer feedback questions during signup receive a referral code with greater impact.</li>
                    <li>• Each successful referral (a new user completing email signup) improves your waitlist position by 1 spot.</li>
                  </ul>
                </div>
                
                <div className="bg-purple-50 border-l-4 border-purple-400 p-4 rounded-r-lg">
                  <h3 className="text-lg font-semibold text-purple-900 mb-2">3. Combined Impact Referral Codes</h3>
                  <ul className="text-purple-800 space-y-1">
                    <li>• Waitlist subscribers receive one unique referral code that can have the impact of both scenarios.</li>
                    <li>• When you complete both email verification and feedback questions, your referral code combines the benefits of both.</li>
                    <li>• This means each successful referral can improve your waitlist position by 1 spot and every two referrals improves your position by 3 spots.</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Policy Notes */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Policy Notes</h2>
              <ul className="text-gray-700 space-y-2 list-disc pl-6">
                <li>We reserve the right to update the referral reward system at any time.</li>
                <li>Any changes will only apply going forward and will not alter the historical impact of your existing referrals or previous conditions.</li>
                <li>Existing referral codes and their current impact levels will remain unchanged for users who have already earned them.</li>
                <li>Improvement to the first waitlist position does not guarantee you will remain in that spot. Other users may surpass you if they achieve more referrals.</li>
              </ul>
            </div>

            {/* Questions */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Questions</h2>
              <p className="text-gray-700">
                If you have any questions about this policy, please contact us at{' '}
                <a 
                  href="mailto:info@quellect.ai" 
                  className="text-primary-600 hover:text-primary-700 font-medium underline"
                >
                  info@quellect.ai
                </a>
                .
              </p>
            </div>
          </div>
        </motion.div>

        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center"
        >
          <button
            onClick={onBack}
            className="bg-white text-gray-900 px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors inline-flex items-center space-x-2"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
        </motion.div>

        {/* Footer */}
        <Footer onTermsClick={() => {}} onPrivacyClick={() => {}} />
      </motion.div>
    </div>
  );
};

export default ReferralCodePolicyPage;
